import { z } from 'zod';
import { IpAddress, IsoTime, ServerRef } from './basics.js';
/**
 * The fleet blocklist (knowledge doc section 12, "Fleet blocklist"; P11.6).
 *
 * CrowdSec's bans end — four hours by default, thirty days at most — and each
 * server's are its own. Bartizo keeps every ban any server reported as history,
 * and an address that earns it by a rule anyone can read (banned on several
 * servers, or again and again on one) goes on a list every server keeps in its
 * own CrowdSec, a new one from its first hour. Rule 11 still holds: the list is
 * decided by that rule, never by a model, and CrowdSec enforces it.
 *
 * The ban grows with each return — the ladder — and ends after a quiet period:
 * addresses are reassigned, and a ban that never ends finds an innocent sooner or
 * later. A permanent place is a person's decision, not the policy's.
 */
export const DAY_SECONDS = 24 * 60 * 60;
/** Section 12's ladder: four hours, a day, a week, thirty days, ninety at most. */
export const DEFAULT_LADDER_SECONDS = [
    4 * 60 * 60,
    DAY_SECONDS,
    7 * DAY_SECONDS,
    30 * DAY_SECONDS,
    90 * DAY_SECONDS,
];
/** Longer than a year is permanent in all but name. */
export const BLOCKLIST_MAX_BAN_SECONDS = 365 * DAY_SECONDS;
/** What a server's CrowdSec is asked to hold; the most active stay when there are more. */
export const BLOCKLIST_MAX_ENTRIES = 20_000;
export const BlocklistPolicy = z
    .object({
    enabled: z.boolean().default(true),
    /** Banned on at least this many servers within the window… */
    min_servers: z.number().int().min(1).max(50).default(2),
    /** …or at least this many times on any one of them. */
    min_bans: z.number().int().min(2).max(100).default(3),
    window_days: z.number().int().min(1).max(90).default(30),
    /** The ban for each time an address earns its place again, in seconds. */
    ladder_seconds: z
        .array(z.number().int().min(60).max(BLOCKLIST_MAX_BAN_SECONDS))
        .min(1)
        .max(10)
        .refine((ladder) => ladder.every((step, i) => i === 0 || step >= (ladder[i - 1] ?? 0)), {
        message: 'each step at least as long as the one before',
    })
        .default([...DEFAULT_LADDER_SECONDS]),
    /** History older than this is forgotten, and an address quiet this long starts at the bottom again. */
    quiet_days: z.number().int().min(7).max(365).default(90),
    max_entries: z
        .number()
        .int()
        .min(100)
        .max(BLOCKLIST_MAX_ENTRIES)
        .default(BLOCKLIST_MAX_ENTRIES),
})
    .strict();
/** The scenario CrowdSec records the list's decisions under; the agent leaves them out of reports. */
export const FLEET_LIST_REASON = 'bartizo:fleet-list';
export const BlocklistSource = z.enum(['auto', 'manual']);
/** `active` is in force; `expired` ended by itself; `removed` a person took it off; `protected` no server may ban it. */
export const BlocklistStatus = z.enum(['active', 'expired', 'removed', 'protected']);
/**
 * What banned an address, as the servers reported it (P11.16):
 *
 * - `trigger`: a CrowdSec trigger rule (Rules page) made CrowdSec ban for a scenario;
 * - `behaviour`: a behaviour rule's own scenario banned it;
 * - `scenario`: one of CrowdSec's own scenarios, with no Bartizo rule involved;
 * - `person`: somebody on the server, with `cscli`;
 * - `console`: a ban from this console (an operator, or an approved suggestion);
 * - `other`: anything else CrowdSec reported.
 */
export const BlocklistCauseKind = z.enum([
    'trigger',
    'behaviour',
    'scenario',
    'person',
    'console',
    'other',
]);
export const BlocklistCause = z.object({
    kind: BlocklistCauseKind,
    /** The rule's id for `trigger`, its name for `behaviour`, the scenario for `scenario`; else words. */
    label: z.string().max(256),
    /** The scenario that fired, when one did. */
    scenario: z.string().max(256).nullable(),
    /** The rule's name as the Rules page shows it, for `trigger` and `behaviour` when it is still there. */
    ruleName: z.string().max(100).nullable().default(null),
    /** How many servers banned it for this, and how often. */
    servers: z.number().int().nonnegative(),
    /** Which, by name (at most twenty): the people reading the list click through. */
    serverRefs: z.array(ServerRef).max(20).default([]),
    bans: z.number().int().nonnegative(),
    lastSeen: IsoTime,
});
export const BlocklistEntry = z.object({
    ip: IpAddress,
    status: BlocklistStatus,
    /** The step of the ladder it is on, from 1. */
    level: z.number().int().min(1).max(10),
    source: BlocklistSource,
    reason: z.string().max(500),
    firstSeen: IsoTime,
    lastSeen: IsoTime,
    until: IsoTime,
    /** Servers it was banned on, and how often, within the window. */
    servers: z.number().int().nonnegative(),
    bans: z.number().int().nonnegative(),
    scenarios: z.array(z.string().max(256)).max(20),
    /** Servers that keep it off their list (their allowlist or proxies). */
    excludedServers: z.number().int().nonnegative(),
    /** Why no server may ban it, when that is so. */
    refused: z.string().max(500).nullable(),
    addedByEmail: z.email().nullable(),
    removedByEmail: z.email().nullable(),
    removedReason: z.string().max(500).nullable(),
    /** What banned it on the servers, most often first (P11.16); empty for a person's own entry. */
    causes: z.array(BlocklistCause).max(10).default([]),
    /** The servers that reported a ban of it, by name, at most twenty. */
    serverRefs: z.array(ServerRef).max(20).default([]),
});
export const BlocklistQuery = z.object({
    status: BlocklistStatus.optional(),
    /** An address, or the start of one. */
    q: z
        .string()
        .trim()
        .max(64)
        .regex(/^[0-9a-fA-F.:]*$/, 'part of an address')
        .optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(200).optional(),
});
export const BlocklistSummary = z.object({
    version: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    /** Servers that applied the current version, and those behind or failing. */
    serversCurrent: z.number().int().nonnegative(),
    serversBehind: z.number().int().nonnegative(),
    policy: BlocklistPolicy,
});
export const BlocklistAddRequest = z
    .object({
    ip: IpAddress,
    reason: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .regex(/^[^\p{Cc}]+$/u, 'no control characters'),
    /** Absent: the ladder's first step. */
    duration_seconds: z.number().int().min(60).max(BLOCKLIST_MAX_BAN_SECONDS).optional(),
})
    .strict();
export const BlocklistRemoveRequest = z
    .object({
    ip: IpAddress,
    reason: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .regex(/^[^\p{Cc}]+$/u, 'no control characters'),
})
    .strict();
/** One address as an agent is handed it: until when, as seconds from now. */
export const AgentBlocklistEntry = z
    .object({ ip: IpAddress, seconds: z.number().int().min(60).max(BLOCKLIST_MAX_BAN_SECONDS) })
    .strict();
/**
 * `GET /v1/agent/blocklist`: this server's copy of the list — without what its
 * allowlist or proxies keep off it, the most recently active first.
 */
export const AgentBlocklist = z.object({
    version: z.number().int().nonnegative(),
    entries: z.array(AgentBlocklistEntry).max(BLOCKLIST_MAX_ENTRIES),
});
/**
 * `POST /v1/agent/blocklist/status`: what the server made of a version, sent as
 * soon as it applied it, so the address page says "stopped here" within minutes.
 */
export const AgentBlocklistStatus = z
    .object({
    version: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative().max(BLOCKLIST_MAX_ENTRIES),
    problem: z.string().max(500).nullable(),
})
    .strict();
/**
 * `sync_fleet_blocklist` (P11.6): make this server's CrowdSec hold exactly these
 * addresses under `FLEET_LIST_REASON` — the whole desired state, so nothing asked
 * removes what the list put there. Addresses only, never ranges.
 */
export const SyncFleetBlocklistArgs = z
    .object({
    entries: z.array(AgentBlocklistEntry).max(BLOCKLIST_MAX_ENTRIES),
    dry_run: z.boolean().optional(),
})
    .strict();
export const FleetBlocklistResult = z.object({
    added: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    kept: z.number().int().nonnegative(),
    /** Refused by this server's CrowdSec allowlists. */
    allowlisted: z.number().int().nonnegative(),
});
//# sourceMappingURL=fleet-blocklist.js.map